import type { IsoDate, Money, PlanDurationUnit } from '@techsofit/api-types';

/** Human label for a plan's billing period, e.g. "1 month", "30 days". */
export function planDuration(unit: PlanDurationUnit, count: number): string {
  const noun = unit === 'MONTH' ? 'month' : 'day';
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** USD money formatter — input is a decimal string from the API ("45.00"). */
export function money(value: Money | number): string {
  const n = typeof value === 'number' ? value : Number(value);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
}

/** Compact money for big metric tiles ("$4.2k"). */
export function moneyCompact(value: Money | number): string {
  const n = typeof value === 'number' ? value : Number(value);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

/** Gyms operate on Zimbabwe time. UTC midnight is still yesterday in Harare. */
export const GYM_ZONE = 'Africa/Harare';

/** Calendar date in gym time, e.g. "2026-09-17". Never use toISOString().slice(0, 10). */
export function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: GYM_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** YYYY-MM of the gym calendar month. */
export function thisMonthIso(): string {
  return todayIso().slice(0, 7);
}

/** Hour 0–23 in gym time (for greetings). */
export function gymHour(): number {
  const hour = new Intl.DateTimeFormat('en-GB', {
    timeZone: GYM_ZONE,
    hour: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(new Date()).find((p) => p.type === 'hour')?.value;
  return Number(hour ?? 0);
}

/** Shift a calendar ISO date by whole days (timezone-safe). */
export function addCalendarDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return dt.toISOString().slice(0, 10);
}

/** Local Y-M-D of a Date that already represents a calendar day (e.g. from parseDate). */
export function dateToIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function today(): Date {
  return parseDate(todayIso());
}

export function parseDate(d: IsoDate): Date {
  return new Date(`${d}T00:00:00`);
}

export function formatDate(d: IsoDate | null, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }): string {
  if (!d) return '—';
  return new Intl.DateTimeFormat('en-GB', opts).format(parseDate(d));
}

/** Numeric dd/mm/yyyy, e.g. "01/07/2026". */
export function formatDMY(d: IsoDate | null): string {
  return formatDate(d, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Whole days between two dates (b - a), ignoring time. */
export function daysBetween(a: Date, b: Date): number {
  const ms = b.setHours(0, 0, 0, 0) - a.setHours(0, 0, 0, 0);
  return Math.round(ms / 86_400_000);
}

/** Human relative phrasing for expiry: "in 5 days", "today", "12 days ago". */
export function relativeDays(days: number | null): string {
  if (days === null) return 'No payments yet';
  if (days === 0) return 'Expires today';
  if (days > 0) return `Expires in ${days} ${days === 1 ? 'day' : 'days'}`;
  const past = Math.abs(days);
  return `Lapsed ${past} ${past === 1 ? 'day' : 'days'} ago`;
}

/** Normalise a local phone to wa.me digits (E.164 without the +). */
export function waDigits(phone: string): string {
  return phone.replace(/[^\d]/g, '');
}
